import { useEffect, useRef, useState } from "react";
import { PencilSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import { LeadListItem } from "./api";

interface Props {
  lead: LeadListItem | null; // null = closed
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}

// Note editor for a lead. Replaces window.prompt: same shell as the other
// Investigations modals (SpeakerModal, MembersModal).
const LeadNoteModal: React.FC<Props> = ({ lead, onClose, onSave }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Reset only when a different lead opens (by id), never because the
  // parent re-rendered with a fresh `onClose` or a refetched lead object.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const leadId = lead?.id ?? null;

  useEffect(() => {
    if (!leadId) return;
    setNote(lead?.note || "");
    setSaving(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  if (!lead) return null;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(note.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <form
        method="dialog"
        className="modal-box max-w-md p-0 overflow-hidden rounded-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-base-300">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <PencilSquareIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">{lead.note ? "Edit note" : "Add a note"}</h3>
            <p className="text-sm text-base-content/60 mt-0.5 line-clamp-2 italic">"{lead.quote_translated || lead.quote}"</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6 py-5">
          <textarea
            autoFocus
            className="textarea textarea-bordered bg-base-100 w-full text-sm leading-6 focus:outline-none focus:border-blue-500"
            rows={4}
            placeholder="What matters about this moment, who to follow up with, what to check…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") save();
            }}
          />
          <p className="mt-1.5 text-[11px] text-base-content/50">Notes are part of the investigation record and appear on the lead sheet. ⌘↵ to save.</p>
        </div>
        <div className="px-6 pb-6 flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={saving}>
            {saving ? "Saving…" : "Save note"}
          </Button>
        </div>
      </form>
      <button type="button" className="modal-backdrop" onClick={() => !saving && onClose()} aria-label="Close" />
    </dialog>
  );
};

export default LeadNoteModal;
